import InfoIcon from '../../../../../ui/svg/common/info';
import React, { useState, ClipboardEvent, KeyboardEvent, ChangeEvent } from 'react';
import { rootActions, useAppDispatch, useAppSelector } from '../../data/store';
import ReferCreditPopup from './credit-popup';
import FormTextField from '../../../../../ui/forms/fields/text-field';
import {
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl
} from '../../../../../domain/validation/validation';
import { addCredit } from '../../data/data-service';
import { validateReferCredit } from '../../domain/shopping-cart-validations';
import { getLocalData } from '../../domain/shopping-cart-address-provider';
import Preloader from '../../../../../ui/svg/common/preloader';
import ValidationErrors from '../../../../../domain/validation/server-errors/validation-errors';
import { sendGAEvent } from '../../../../../domain/marketing/gtm-provider';

const CreditForm = () => {
    const dispatch = useAppDispatch();

    const isReferCreditChecked = useAppSelector(store => store.root.isReferCreditChecked);
    const referCreditControl = useAppSelector(store => store.root.referCreditControl);
    const cartStep = useAppSelector(store => store.root.cartStep);
    const showCreditNavError = useAppSelector(store => store.root.showCreditNavError);

    const [ creditPopupVisible, setCreditPopupVisible ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ serverErrors, setServerErrors ] = useState<string[]>([]);


    // CHECKBOXES ------------------------------------
    const onCreditCheckboxChange = () => {
        setServerErrors([]);

        dispatch(
            rootActions.main({
                isReferCreditChecked: !isReferCreditChecked,
                showCreditNavError: false,
            })
        );
    };

    const showCreditPopup = () => {
        setCreditPopupVisible(true);
    };

    const hideCreditPopup = () => {
        setCreditPopupVisible(false);
    };

    // TEXT-BOX ---------------------------------------

    const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            rootActions.setControl({
                controlName: 'referCreditControl',
                control: updateControl(
                    referCreditControl,
                    (evt.target as HTMLInputElement).value,
                    !referCreditControl.isValid
                )
            })
        );

        dispatch(
            rootActions.main({
                showCreditNavError: false,
            })
        );
    };

    const handleKeyUp = async (evt: KeyboardEvent<HTMLInputElement>) => {

        if(evt.key === 'Enter') {
            await apply();
            return;
        }

        if(evt.key === 'Escape') {
            setServerErrors([]);

            dispatch(
                rootActions.setControl({
                    controlName: 'referCreditControl',
                    control: updateControl(referCreditControl, '', !referCreditControl.isValid)
                })
            );

            dispatch(
                rootActions.main({
                    showCreditNavError: false,
                })
            );

            /*
            // remove coupon value from the text box
            dispatch(
                couponActions.setCouponControl(
                    updateControl(couponControl, '', false)
                )
            );
             */
        }
    };

    const handlePaste = (evt: ClipboardEvent<HTMLInputElement>) => {
        dispatch(
            rootActions.setControl({
                controlName: 'referCreditControl',
                control: updateControl(
                    referCreditControl,
                    removeInvisibleCharsOnPaste(evt),
                    !referCreditControl.isValid
                )
            })
        );
    };

    const handleBlur = () => {
        dispatch(
            rootActions.setControl({
                controlName: 'referCreditControl',
                control: validateControl(referCreditControl),
            })
        );
    };

    const handleClick = () => {
        setServerErrors([]);

        dispatch(
            rootActions.main({
                showCreditNavError: false,
            })
        );
    };

    // SERVER ----------------------------------------

    const apply = async () => {
        const isReferValid = validateReferCredit(referCreditControl, dispatch);
        if(!isReferValid) return;

        setServerErrors([]);

        dispatch(
            rootActions.main({
                showCreditNavError: false,
            })
        );

        setLoading(true);

        const response = await addCredit(
            Number(referCreditControl.value) || 0,
            // isReferRecurringChecked,
            getLocalData()
        );

        setLoading(false);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
            setServerErrors(response?.data?.errors || ['An error has occurred.']);
            return;
        }

        // update the whole cart data
        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );

        dispatch(
            rootActions.main({
                isReferCreditChecked: false,
            })
        );

        sendGAEvent('Apply Credit', `Step ${ cartStep }`);
    };

    return (
        <div className="flex flex-col mt-4">
            <label className="flex items-center">
                <input
                    type="checkbox"
                    checked={ isReferCreditChecked }
                    onChange={ onCreditCheckboxChange }
                    aria-label="Redeem My Rewards"
                />
                <span className="mx-2 cursor-pointer">Redeem My Rewards</span>
                <button
                    title="Learn more about rewards"
                    type="button"
                    onClick={ showCreditPopup }
                    className="flex items-center">
                    <InfoIcon width="15px" color="#777" />
                </button>
            </label>

            {
                isReferCreditChecked &&
                <div className="mt-2 flex flex-col">
                    <div className="flex">
                        <span className="mr-1" style={{
                            marginTop: '0.65rem',
                        }}>$</span>

                        <FormTextField
                            id={ 'referCreditControl' }
                            label={ 'Credit' }
                            control={ referCreditControl }
                            maxLength={ 200 }
                            textBoxClasses="rounded bg-white border-green text-gray-500"
                            rootClasses="mb-0 flex-1"
                            onChange={ handleChange }
                            onBlur={ handleBlur }
                            onKeyUp={ handleKeyUp }
                            onPaste={ handlePaste }
                            onClick={ handleClick }
                        />
                    </div>
                    <div className="flex justify-end">
                        {
                            !loading &&
                            <button
                                aria-label="Apply my rewards"
                                type="button"
                                onClick={ apply }
                                className="cursor-pointer text-gray-700 font-bold">
                                Apply
                            </button>
                        }

                        { loading && <Preloader width="24px" height="24px" color="#4f4f4f" /> }
                    </div>

                    {
                        serverErrors && serverErrors.length > 0 && (
                            <div className="error-box text-sm mt-2" role="alert">
                                <ValidationErrors errors={ serverErrors } />
                            </div>
                        )
                    }

                    {
                        showCreditNavError &&
                        <div className="error-box my-2" role="alert">
                            <p>Please submit your rewards by clicking Apply, or uncheck the "Redeem My Rewards" checkbox.</p>
                        </div>
                    }
                </div>
            }

            { creditPopupVisible && <ReferCreditPopup close={ hideCreditPopup } /> }
        </div>
    )
};

export default CreditForm;